import { Suspense } from 'react'
import type { MenuProps } from 'antd'
import { Layout, Menu, Spin } from 'antd'
import './MainLayout.scss'
import { Header } from '../header/Header'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Footer } from '../footer/Footer'
import {
  CalendarOutlined,
  FileDoneOutlined,
  ReconciliationOutlined,
  SettingOutlined,
  SyncOutlined,
  TableOutlined,
  UnorderedListOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { RoleRights } from '@/provider/roles'
import { hasRightAssigned } from '@/features/order/orderAccess'
import { BusinessUnit, ProductionSite } from '@/features/order/order'
import { useRoleContext } from '@/provider/RoleProvider'
import { useTranslation } from 'react-i18next'
import { getFeatureToggleConfiguration } from '@/configuration/featureToggleConfiguration'
import { msalInstance } from '@/auth/mintAuthenticationProvider'

const { Content, Sider } = Layout

type MenuItem = Required<MenuProps>['items'][number]

export const MainLayout = () => {
  const location = useLocation()
  const { t } = useTranslation('common', { keyPrefix: 'main-layout' })
  const { data: featureToggles } = getFeatureToggleConfiguration()

  const clearStorageAndRefresh = async () => {
    try {
      const instance = await msalInstance()
      await instance.clearCache()
      console.log('MSAL cache cleared successfully')
    } catch (error) {
      console.warn('Could not clear MSAL cache:', error)
    }
    localStorage.clear()
    window.location.reload()
  }

  const navigate = useNavigate()
  const { selectedSite, getSitesWithEditRightsOfType } = useRoleContext()

  const hasNgsAdminRights = hasRightAssigned([BusinessUnit.NGS], RoleRights.ADMIN)
  const hasNgsAdminRightsAtEbeSite = hasNgsAdminRights && selectedSite === ProductionSite.EBE
  const hasMicroArraysEditRights = getSitesWithEditRightsOfType(BusinessUnit.MICRO_ARRAY).includes(selectedSite)
      && hasRightAssigned([BusinessUnit.MICRO_ARRAY], RoleRights.EDIT)

  const settingsItems: MenuItem[] = [
    {
      key: '/settings/manifest-schemas',
      label: t('manifest-schemas'),
      disabled: !featureToggles?.manualOrders || !hasRightAssigned([BusinessUnit.NGS, BusinessUnit.MICRO_ARRAY], RoleRights.EDIT),
      icon: <TableOutlined />,
      onClick: () => navigate('/settings/manifest-schemas')
    },
    {
      key: '/settings/kit-components',
      label: t('kit-components'),
      disabled: !hasNgsAdminRightsAtEbeSite,
      icon: <UploadOutlined/>,
      onClick: () => navigate('/settings/kit-components')
    },
    {
      key: '/settings/order-automation',
      label: t('order-automation'),
      disabled: !hasNgsAdminRightsAtEbeSite,
      icon: <UploadOutlined/>,
      onClick: () => navigate('/settings/order-automation')
    },
    {
      key: '/settings/holidays',
      label: t('holiday-management'),
      disabled: !hasNgsAdminRights,
      icon: <CalendarOutlined />,
      onClick: () => navigate('/settings/holidays')
    },
    {
      key: '/settings/reports',
      label: t('report-configuration'),
      disabled: !hasMicroArraysEditRights,
      icon: <FileDoneOutlined />,
      onClick: () => navigate('/settings/reports')
    }
  ].filter(item => !item.disabled)

  const items: MenuItem[] = [
    {
      key: '/',
      label: t('orders'),
      icon: <TableOutlined/>,
      onClick: () => navigate('/')
    },
    {
      key: '/projects',
      label: t('projects'),
      icon: <ReconciliationOutlined />,
      onClick: () => navigate('/projects')
    },
    {
      key: '/tasks',
      label: t('tasks'),
      icon: <UnorderedListOutlined/>,
      disabled: !hasRightAssigned([BusinessUnit.NGS], RoleRights.READ),
      onClick: () => navigate('/tasks')
    },
    {
      key: '/relogin',
      label: t('re-login'),
      icon: <SyncOutlined/>,
      onClick: () => clearStorageAndRefresh()
    },
    {
      key: '/settings',
      label: t('settings'),
      icon: <SettingOutlined/>,
      disabled: settingsItems.length === 0,
      children: settingsItems
    }
  ].filter(item => !item.disabled)

  const splitIntoKeys = (input: string[]) => {
    const result = []
    let cumulation = ''
    for (const value of input) {
      cumulation += '/' + value
      result.push(cumulation)
    }
    return result
  }

  //splitIntoKeys('/settings/kit-components') returns ['/', '/settings', '/settings/kit-components'], but we don't need the first slash element, as it will mark incorrect item as selected
  const selectedKeys = splitIntoKeys(location.pathname.split('/').slice(1))

  return (
    <Layout className="app-layout">
      <Header/>
      <Layout>
        <Sider
          theme="light"
          breakpoint={'xxl'}
          collapsible
          width={240}>
          <Menu
            theme="light"
            defaultSelectedKeys={selectedKeys}
            defaultOpenKeys={selectedKeys}
            mode="inline"
            items={items}
          />
        </Sider>
        <Layout>
          <Content className="content-layout">
            <Suspense fallback={<Spin/>}>
              <div className="outlet">
                <Outlet/>
              </div>
            </Suspense>
          </Content>
        </Layout>
      </Layout>
      <Footer/>
    </Layout>
  )
}
